import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Spinner } from 'react-bootstrap';
import Image from './Image';
import MemoryTextArea from './MemoryTextArea';
import { IBrandVoiceState } from '../../interfaces/brand-magic.interface';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { getActiveBrand } from '../../store/selector/brand.selector';
import { useSaveBrandVoiceMutation } from '../../store/api/brandMagic.api';
import { useUpdateBrandOnBoardingStatusMutation } from '../../store/api/brand.api';
import { SetActiveBrandOnBoardedStatus } from '../../store/slice/brand.slice';
import { displayErrorToast } from '../../utils/ToastUtils';
import { brandVoiceLabels } from '../../constants/brandMagic';

interface IProps {
    handlePrev: () => void;
    brandVoice: IBrandVoiceState;
    setBrandVoice: React.Dispatch<React.SetStateAction<IBrandVoiceState>>;
    isFetchingData: boolean;
}

const Memory = ({ handlePrev, brandVoice, setBrandVoice, isFetchingData }: IProps) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const activeBrand = useAppSelector(getActiveBrand);
    const [anythingElseContent, setAnythingElseContent] = useState<string>('');
    const [mimicContent, setMimicContent] = useState<string>('');
    const [saveLoading, setSaveLoading] = useState<boolean>(false);
    const [saveBrandVoice] = useSaveBrandVoiceMutation();
    const [updateBrandOnBoardingStatus] = useUpdateBrandOnBoardingStatusMutation();
    const handleAnythingElseContentChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (event && event.target) {
            setAnythingElseContent(event.target.value);
        }
    };
    const handleMimicContentChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (event && event.target) {
            setMimicContent(event.target.value);
        }
    };

    const handleFinish = async () => {
        try {
            if (!activeBrand) return;
            await handleSave();
            const data = await updateBrandOnBoardingStatus(activeBrand.id);
            dispatch(SetActiveBrandOnBoardedStatus(true));
            navigate('/');
        } catch {
            displayErrorToast('Failed to setup please try again!');
        }
    };

    const handleSave = async () => {
        if ((mimicContent.length > 0 || anythingElseContent.length > 0) && !saveLoading) {
            setSaveLoading(true);
            const newBrandVoice = {
                ...brandVoice,
                scrapedTextContent: mimicContent,
                anythingElse: anythingElseContent,
            };
            setBrandVoice(newBrandVoice);
            await saveBrandVoice(newBrandVoice)
                .unwrap()
                .then((response: any) => {
                    setSaveLoading(false);
                })
                .catch((error) => {
                    setSaveLoading(false);
                });
        }
    };

    useEffect(() => {
        if (
            brandVoice &&
            brandVoice.scrapedTextContent &&
            brandVoice.scrapedTextContent.length > 0
        ) {
            setMimicContent(brandVoice.scrapedTextContent);
        } else setMimicContent('');
        if (brandVoice && brandVoice.anythingElse && brandVoice.anythingElse.length > 0) {
            setAnythingElseContent(brandVoice.anythingElse);
        } else setAnythingElseContent('');
    }, [brandVoice]);

    return (
        <>
            <div className="brandMagic_content">
                <div className="content_potions">
                    <div className="potion-alt">
                        {isFetchingData ? (
                            <div className="d-flex justify-content-center mt-3">
                                <Spinner animation="border" />
                            </div>
                        ) : (
                            <>
                                <div>
                                    <div className="content_header">
                                        <div>
                                            <h1 className="fw-bold text-primary mb-3">Memory</h1>
                                        </div>
                                        <Form.Group className="form mb-4 mt-2">
                                            <Form.Label className="h4 text-primary fw-bold m-0">
                                                {brandVoiceLabels['anythingElse']}
                                            </Form.Label>
                                            <div className="whitespace"></div>
                                            <MemoryTextArea
                                                value={anythingElseContent}
                                                className={'anythingElse'}
                                                id={'anythingElse'}
                                                onChange={handleAnythingElseContentChange}
                                                rows={1}
                                                readOnly={saveLoading}
                                                required={true}
                                                onBlur={() => {}}
                                            />
                                        </Form.Group>
                                    </div>
                                </div>
                                <div className="content_header">
                                    <div>
                                        <h1 className="fw-bold text-primary">Writing Sample</h1>
                                    </div>
                                    <div className="mb-3">
                                        {brandVoice &&
                                            brandVoice.scrapedTextContent &&
                                            brandVoice.scrapedTextContent.length > 0 && (
                                                <p
                                                    className="h5 text-light m-0 fw-regular mb-3"
                                                    style={{ fontSize: '0.85rem' }}
                                                >
                                                    To learn your brand voice, I tried to prefill
                                                    this section with text from your website. Feel
                                                    free to replace it now or later.
                                                </p>
                                            )}
                                    </div>
                                    <Form.Group className="form mb-4 mt-2">
                                        <Form.Label className="h4 text-primary fw-bold m-0">
                                            {brandVoiceLabels['scrapedTextContent']}
                                        </Form.Label>
                                        <div className="whitespace"></div>
                                        <MemoryTextArea
                                            value={mimicContent}
                                            className={'scrapedTextContent'}
                                            id={'scrapedTextContent'}
                                            onChange={handleMimicContentChange}
                                            rows={1}
                                            readOnly={saveLoading}
                                            required={true}
                                            onBlur={() => {}}
                                            onKeyPress={() => {}}
                                        />
                                    </Form.Group>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                <Image />
            </div>
            <div className="d-flex justify-content-between align-items-center mt-3 brandMagic_buttons">
                <Button onClick={handlePrev} variant="secondary" size="lg">
                    Back
                </Button>
                {saveLoading ? (
                    <Button variant="primary" size="lg">
                        <Spinner />
                    </Button>
                ) : brandVoice &&
                  brandVoice.keyProductService &&
                  brandVoice.scrapedTextContent === mimicContent &&
                  brandVoice.anythingElse === anythingElseContent ? (
                    <Button variant="primary" size="lg" onClick={handleFinish}>
                        Finish
                    </Button>
                ) : (
                    <div className="d-flex justify-content-between align-items-center">
                        <Button
                            variant="primary"
                            size="lg"
                            onClick={handleSave}
                            style={{ marginRight: '5px' }}
                        >
                            Save
                        </Button>
                        <Button variant="primary" size="lg" onClick={handleFinish}>
                            Finish
                        </Button>
                    </div>
                )}
            </div>
        </>
    );
};

export default Memory;
